import React, { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import Paginacion from '../../components/common/Paginacion';
import usePaginacion from '../../hooks/usePaginacion';
import { useToast } from '../../context/ToastContext';
import ingresoService from '../../services/ingresoService';
import categoriaService from '../../services/categoriaService';
import { formatearMoneda, formatearFecha } from '../../utils/helpers';
import { validarMonto, validarTexto, validarFecha } from '../../utils/validaciones';
import { FaPlus, FaEdit, FaTrash, FaArrowUp, FaSearch } from 'react-icons/fa';

const hoy = (() => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
})();

const FORM_INICIAL = {
  categoria_id: '', monto: '', moneda: 'PEN',
  fecha: hoy, concepto: '', observaciones: '',
};
export default function IngresosPage() {
  const { mostrar }                     = useToast();
  const [ingresos,     setIngresos]     = useState([]);
  const [categorias,   setCategorias]   = useState([]);
  const [cargando,     setCargando]     = useState(true);
  const [guardando,    setGuardando]    = useState(false);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [editando,     setEditando]     = useState(null);
  const [filtroMes,    setFiltroMes]    = useState('');
  const [busqueda,     setBusqueda]     = useState('');
  const [form,         setForm]         = useState(FORM_INICIAL);
  const [errores,      setErrores]      = useState({});

  useEffect(() => { cargarDatos(); }, []);

  const cargarDatos = async () => {
    setCargando(true);
    try {
      const [ing, cat] = await Promise.all([
        ingresoService.listar(),
        categoriaService.listar('ingreso'),
      ]);
      setIngresos(ing.data || []);
      setCategorias(cat);
    } finally {
      setCargando(false);
    }
  };

  const ingresosFiltrados = ingresos.filter((i) => {
    const coincideMes      = !filtroMes || i.fecha?.startsWith(filtroMes);
    const coincideBusqueda = !busqueda ||
      i.concepto?.toLowerCase().includes(busqueda.toLowerCase()) ||
      i.categoria?.nombre?.toLowerCase().includes(busqueda.toLowerCase());
    return coincideMes && coincideBusqueda;
  });

  const { datosPaginados, pagina, totalPaginas, cambiarPagina, setPagina } =
    usePaginacion(ingresosFiltrados);

  const totalIngresos = ingresosFiltrados
    .filter((i) => i.estado === 'activo')
    .reduce((sum, i) => sum + parseFloat(i.monto || 0), 0);

  const abrirModal = (ingreso = null) => {
    setEditando(ingreso);
    setErrores({});
    setForm(ingreso ? {
      categoria_id:  ingreso.categoria_id,
      monto:         ingreso.monto,
      moneda:        ingreso.moneda,
      fecha:         ingreso.fecha,
      concepto:      ingreso.concepto,
      observaciones: ingreso.observaciones || '',
    } : FORM_INICIAL);
    setMostrarModal(true);
  };

  const cerrarModal = () => {
    setMostrarModal(false);
    setEditando(null);
    setForm(FORM_INICIAL);
    setErrores({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errores[name]) setErrores((prev) => ({ ...prev, [name]: null }));
  };

  const validar = () => {
    const e = {};
    const monto    = validarMonto(form.monto);
    const concepto = validarTexto(form.concepto, 'Concepto', 3);
    const fecha    = validarFecha(form.fecha);
    if (monto)    e.monto    = monto;
    if (concepto) e.concepto = concepto;
    if (fecha)    e.fecha    = fecha;
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const erroresForm = validar();
    if (Object.keys(erroresForm).length > 0) {
      setErrores(erroresForm);
      return;
    }
    setGuardando(true);
    try {
      if (editando) {
        await ingresoService.actualizar(editando.id, form);
        mostrar('Ingreso actualizado correctamente.');
      } else {
        await ingresoService.crear(form);
        mostrar('Ingreso registrado correctamente.');
      }
      cerrarModal();
      cargarDatos();
    } catch (err) {
      mostrar(err.response?.data?.message || 'Error al guardar el ingreso.', 'danger');
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar este ingreso?')) return;
    try {
      await ingresoService.eliminar(id);
      mostrar('Ingreso eliminado correctamente.');
      cargarDatos();
    } catch {
      mostrar('Error al eliminar el ingreso.', 'danger');
    }
  };

  return (
    <Layout>
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <h4>Ingresos</h4>
          <p>Registro y control de ingresos financieros</p>
        </div>
        <button className="btn btn-danger" onClick={() => abrirModal()}>
          <FaPlus className="me-2" /> Nuevo Ingreso
        </button>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-md-3">
          <div className="card card-custom">
            <div className="card-body py-3">
              <div className="d-flex align-items-center gap-3">
                <div className="rounded p-2"
                  style={{ background: 'rgba(39,174,96,0.15)' }}>
                  <FaArrowUp color="#27AE60" size={20} />
                </div>
                <div>
                  <div className="small text-muted">Total</div>
                  <div className="fw-bold text-success">
                    {formatearMoneda(totalIngresos)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card card-custom">
            <div className="card-body py-3">
              <div className="input-group input-group-sm">
                <span className="input-group-text"><FaSearch /></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por concepto o categoría..."
                  value={busqueda}
                  onChange={(e) => { setBusqueda(e.target.value); setPagina(1); }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card card-custom">
            <div className="card-body py-3">
              <input
                type="month"
                className="form-control form-control-sm"
                value={filtroMes}
                onChange={(e) => { setFiltroMes(e.target.value); setPagina(1); }}
              />
            </div>
          </div>
        </div>

        <div className="col-md-2">
          <div className="card card-custom">
            <div className="card-body py-3 text-center">
              <div className="small text-muted">Registros</div>
              <div className="fw-bold">{ingresosFiltrados.length}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card card-custom">
        <div className="card-body">
          {cargando ? (
            <div className="text-center py-4">
              <div className="spinner-custom mx-auto" />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-custom">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Concepto</th>
                      <th>Categoría</th>
                      <th>Monto</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datosPaginados.length > 0 ? (
                      datosPaginados.map((i) => (
                        <tr key={i.id}>
                          <td>{formatearFecha(i.fecha)}</td>
                          <td className="fw-bold">{i.concepto}</td>
                          <td>
                            <span className="badge bg-secondary">
                              {i.categoria?.nombre || '—'}
                            </span>
                          </td>
                          <td className="text-success fw-bold">
                            {formatearMoneda(i.monto)}
                          </td>
                          <td>
                            <span className={`badge ${i.estado === 'activo' ? 'badge-activo' : 'badge-anulado'}`}>
                              {i.estado}
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={() => abrirModal(i)}
                            ><FaEdit /></button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleEliminar(i.id)}
                            ><FaTrash /></button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center text-muted py-4">
                          <FaArrowUp size={32} className="mb-2 d-block mx-auto" />
                          Sin ingresos registrados
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {ingresosFiltrados.length > 0 && (
                    <tfoot>
                      <tr className="table-success">
                        <td colSpan="3" className="fw-bold text-end">Total:</td>
                        <td className="fw-bold text-success">
                          {formatearMoneda(totalIngresos)}
                        </td>
                        <td colSpan="2" />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              <Paginacion
                paginaActual={pagina}
                totalPaginas={totalPaginas}
                onCambiar={cambiarPagina}
              />
            </>
          )}
        </div>
      </div>

      {mostrarModal && (
        <div className="modal-overlay">
          <div className="modal-custom">
            <div className="modal-header-custom">
              <h5>{editando ? 'Editar Ingreso' : 'Nuevo Ingreso'}</h5>
              <button className="btn-close-custom" onClick={cerrarModal}>×</button>
            </div>
            <div className="modal-body-custom">
              <form onSubmit={handleSubmit}>
                <div className="row g-3">

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Categoría *</label>
                    <select
                      name="categoria_id"
                      className="form-select"
                      value={form.categoria_id}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Seleccionar</option>
                      {categorias.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label fw-bold">Monto *</label>
                    <input
                      name="monto"
                      type="number"
                      step="0.01"
                      min="0.01"
                      className={`form-control ${errores.monto ? 'is-invalid' : ''}`}
                      value={form.monto}
                      onChange={handleChange}
                    />
                    {errores.monto && (
                      <div className="invalid-feedback">{errores.monto}</div>
                    )}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label fw-bold">Moneda</label>
                    <select
                      name="moneda"
                      className="form-select"
                      value={form.moneda}
                      onChange={handleChange}
                    >
                      <option value="PEN">Soles (PEN)</option>
                      <option value="USD">Dólares (USD)</option>
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Fecha *</label>
                    <input
                      name="fecha"
                      type="date"
                      className={`form-control ${errores.fecha ? 'is-invalid' : ''}`}
                      value={form.fecha}
                      onChange={handleChange}
                    />
                    {errores.fecha && (
                      <div className="invalid-feedback">{errores.fecha}</div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Concepto *</label>
                    <input
                      name="concepto"
                      className={`form-control ${errores.concepto ? 'is-invalid' : ''}`}
                      value={form.concepto}
                      onChange={handleChange}
                      placeholder="Mínimo 3 caracteres"
                    />
                    {errores.concepto && (
                      <div className="invalid-feedback">{errores.concepto}</div>
                    )}
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-bold">Observaciones</label>
                    <textarea
                      name="observaciones"
                      className="form-control"
                      rows={2}
                      value={form.observaciones}
                      onChange={handleChange}
                    />
                  </div>

                </div>

                <div className="d-flex gap-2 justify-content-end mt-4">
                  <button type="button" className="btn btn-secondary" onClick={cerrarModal}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-danger" disabled={guardando}>
                    {guardando ? 'Guardando...' : editando ? 'Actualizar' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}